import React, { useEffect, useState } from 'react';
import { Box, Buildings, Crown } from '@solar-icons/react';
import { legacyVaultService, LegacyVaultItem } from '../../../services/legacyVaultService';
import { useUserContext } from '../../../contexts/UserContext';
import LegacyVaultPricingSkeleton from './LegacyVaultPricingSkeleton';
import { LoadingStatus } from '../../Skeleton';
import { showSuccessNotification, showErrorNotification } from '../../../utils/notificationUtils';
import { useTranslation } from 'react-i18next';

const LegacyVaultPricing: React.FC = () => {
    const { t } = useTranslation();
    const { userId } = useUserContext();
    const [plans, setPlans] = useState<LegacyVaultItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [processingPlanId, setProcessingPlanId] = useState<string | null>(null);

    const getIcon = (planId: string) => {
        switch (planId) {
            case 'essential':
                return <Box size={32} color="#2a6355" />;
            case 'heritage':
                return <Buildings size={32} color="#2a6355" />;
            case 'dynasty':
                return <Crown size={32} color="#f38173" />;
            default:
                return <Box size={32} color="#2a6355" />;
        }
    };

    useEffect(() => {
        const fetchPlans = async () => {
            if (!userId) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const fetchedPlans = await legacyVaultService.fetchLegacyVaults(userId);
                setPlans(fetchedPlans);
            } catch (err) {
                console.error('Error fetching legacy vault plans:', err);
                setError(t('lv_page.pricing.failed_to_load_plans'));
            } finally {
                setLoading(false);
            }
        };

        fetchPlans();
    }, [userId]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const success = urlParams.get('success');
        const hash = window.location.hash;

        if (hash === '#pricing') {
            const pricingElement = document.getElementById('pricing');
            if (pricingElement) {
                pricingElement.scrollIntoView({ behavior: 'smooth' });
            }
        }

        if (success !== null) {
            if (success === 'true') {
                showSuccessNotification(t('lv_page.pricing.payment_successful'));
            } else {
                showErrorNotification(t('lv_page.pricing.payment_failed'));
            }

            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('success');
            window.history.replaceState({}, '', newUrl.toString());

            if (userId) {
                legacyVaultService.fetchLegacyVaults(userId).then(setPlans).catch(console.error);
            }
        }
    }, [userId]);

    const handlePlanSelect = async (planId: string) => {
        if (!userId) return;

        const plan = plans.find((p) => p.id === planId);
        if (!plan) return;

        if (plan.hasAccess) {
            if (plan.files && plan.files.length > 0) {
                plan.files.forEach((fileUrl, index) => {
                    const link = document.createElement('a');
                    link.href = fileUrl;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                });
            } else {
                showErrorNotification(t('lv_page.pricing.download_files_not_available'));
            }
            return;
        }

        try {
            setProcessingPlanId(planId);

            const baseUrl = window.location.origin;
            const successUrl = `${baseUrl}/dashboard/tools/legacy-vault?success=true#pricing`;
            const cancelUrl = `${baseUrl}/dashboard/tools/legacy-vault?success=false#pricing`;

            const paymentUrl = await legacyVaultService.createPaymentSession(planId, userId, successUrl, cancelUrl);

            window.location.assign(paymentUrl);
        } catch (error) {
            console.error('Error creating payment session:', error);
            showErrorNotification(t('lv_page.pricing.failed_to_start_payment'));
        } finally {
            setProcessingPlanId(null);
        }
    };

    if (loading) {
        return (
            <LoadingStatus label={t('lv_page.pricing.loading_plans', { defaultValue: 'Loading pricing plans' })}>
                <LegacyVaultPricingSkeleton />
            </LoadingStatus>
        );
    }

    if (error) {
        return (
            <section className="py-12 lg:py-16" id="pricing">
                <div className="mx-auto max-w-[1142px] px-4">
                    <div className="text-center">
                        <h2 className="mb-5 text-[26px]/7 font-bold text-red-600 dark:text-red-400">{t('lv_page.pricing.error_loading_plans')}</h2>
                        <p className="text-lg text-gray-600 dark:text-gray-300">{error}</p>
                        <button onClick={() => window.location.reload()} className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                            {t('retry')}
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="py-12 lg:py-16" id="pricing">
            <div className="mx-auto max-w-[1142px] px-4">
                <div className="mx-auto pb-14 text-center lg:w-2/3 lg:pb-24">
                    <h2 className="mb-5 text-[26px]/7 font-bold text-green-700 dark:text-white">{t('lv_page.pricing.which_legacy_will_you_leave')}</h2>
                    <p className="text-lg md:text-xl">{t('lv_page.pricing.choose_the_perfect_plan')}</p>
                </div>

                <div className="grid gap-8 lg:grid-cols-3">
                    {plans.map((plan, index) => {
                        const isMiddlePlan = index === 1;
                        const isPopular = isMiddlePlan;

                        return (
                            <div
                                key={plan.id}
                                className={`group relative flex h-full flex-col rounded-2xl border-2 p-8 transition-all duration-300 hover:shadow-xl ${isPopular
                                    ? 'border-green-500 bg-green-50 dark:border-green-400 dark:bg-green-700/30'
                                    : 'border-green-200 bg-white dark:border-green-900/50 dark:bg-green-900/20'
                                    }`}
                                data-aos="fade-up"
                                data-aos-duration="1000"
                                data-aos-delay={index * 200}
                                id={`lvCard-${plan.id}`}
                            >
                                {isPopular && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                        <span className="rounded-full bg-green-500 px-4 py-1 text-sm font-semibold text-white dark:text-black">{t('most_popular')}</span>
                                    </div>
                                )}

                                <div className="mb-6 text-center">
                                    <div className="mb-4 flex justify-center">{getIcon(plan.id)}</div>
                                    <h3 className="mb-2 text-2xl font-bold text-green-700 dark:text-white">{plan.name}</h3>
                                    <div className="mb-4">
                                        <span className="text-4xl font-bold text-green-700 dark:text-white">${(plan.price / 100).toFixed(0)}</span>
                                    </div>
                                    <p className="text-gray-600 dark:text-gray-300">{plan.description}</p>
                                </div>

                                <ul className="mb-8 flex-grow space-y-3">
                                    {plan.features.map((feature: string, featureIndex: number) => (
                                        <li key={featureIndex} className="flex items-center gap-3">
                                            <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                                <path
                                                    fillRule="evenodd"
                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                            <span className="text-gray-600 dark:text-gray-300">{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-auto">
                                    <button
                                        onClick={() => handlePlanSelect(plan.id)}
                                        disabled={processingPlanId === plan.id}
                                        className={`lv-select-button  dark:text-black  group w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors flex items-center justify-center gap-2 ${
                                            plan.hasAccess
                                                ? 'bg-primary hover:bg-primary/90 dark:bg-primary dark:hover:bg-primary/90'
                                                : isPopular
                                                ? 'bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600'
                                                : 'bg-secondary hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700'
                                        } ${processingPlanId === plan.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        id={`lvSelect-${plan.id}`}
                                        data-plan-id={plan.id}
                                        aria-label={`Select ${plan.name} plan`}
                                        data-testid={`lvSelect-${plan.id}`}
                                    >
                                        {processingPlanId === plan.id ? (
                                            <>
                                                <svg className="animate-spin-tw h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                    ></path>
                                                </svg>
                                                {t('processing')}...
                                            </>
                                        ) : plan.hasAccess ? (
                                            <>
                                                <span>{t('lv_page.pricing.download_vault')}</span>
                                                <span className="relative start-0 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/20 duration-300 group-hover:start-1">
                                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                        <path d="M6 1V9M6 9L3 6M6 9L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </span>
                                            </>
                                        ) : (
                                                <>{t('lv_page.pricing.choose_plan', { planName: plan.name })}</>
                                        )}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default LegacyVaultPricing;
